import { getObserver } from '@theme/theme-global';

if (!customElements.get('floating-images')) {
  class FloatingImages extends HTMLDivElement {
    static LERP_FACTOR = 0.22;
    static PROGRESS_EPSILON = 0.0001;

    constructor() {
      super();
      this.section = this.closest('.shopify-section');
      this.images = this.querySelectorAll('[data-floating-image]');
      this.currentProgress = 0;
      this.targetProgress = 0;
      this.sectionWidth = 0;
      this.sectionHeight = 0;
      this.scrollStartY = 0;
      this.scrollTotalDist = 1;
      this.viewportHeight = 0;
      this.loopActive = false;
      this.renderRaf = null;
      this.animationReady = false;

      this.boundOnScroll = this.onScroll.bind(this);
      this.boundOnResize = this.onResize.bind(this);
      this.boundTick = this.tick.bind(this);
      this.boundInitAnimation = this.initAnimation.bind(this);

      this._observer = getObserver({ rootMargin: '200px 0px', threshold: 0 }, true, false);
      this._observer.observe(this.section, () => {
        this.initAnimation();
      });
    }

    initAnimation() {
      if (this.images.length === 0) return;

      if (!this.animationReady) {
        this.animationReady = true;

        if (this.images.length > 0) this.images[0].style.willChange = 'transform';
        if (this.images.length > 2) this.images[this.images.length - 1].style.willChange = 'transform';

        window.addEventListener('scroll', this.boundOnScroll, { passive: true });
        window.addEventListener('resize', this.boundOnResize, { passive: true });

        if (window.lenis) {
          this.boundLenisScroll = this.onScroll.bind(this);
          window.lenis.on('scroll', this.boundLenisScroll);
        }
      }

      this.cacheMetrics();
      this.startLoop();
    }

    connectedCallback() {
      if (Shopify.designMode) {
        document.addEventListener('shopify:section:select', this.boundInitAnimation);
        document.addEventListener('shopify:section:reorder', this.boundInitAnimation);
        document.addEventListener('shopify:section:load', this.boundInitAnimation);
      }
    }

    disconnectedCallback() {
      this.stopLoop();
      window.removeEventListener('scroll', this.boundOnScroll);
      window.removeEventListener('resize', this.boundOnResize);

      if (window.lenis && this.boundLenisScroll) {
        window.lenis.off('scroll', this.boundLenisScroll);
      }

      if (Shopify.designMode) {
        document.removeEventListener('shopify:section:select', this.boundInitAnimation);
        document.removeEventListener('shopify:section:reorder', this.boundInitAnimation);
        document.removeEventListener('shopify:section:load', this.boundInitAnimation);
      }
    }

    onScroll() {
      this.startLoop();
    }

    onResize() {
      this.cacheMetrics();
      this.startLoop();
    }

    cacheMetrics() {
      const rect = this.section.getBoundingClientRect();
      this.sectionWidth = rect.width;
      this.sectionHeight = rect.height;
      this.viewportHeight = window.innerHeight;
      this.scrollStartY = this.viewportHeight * 0.5;
      const endY = this.viewportHeight - 150 - this.sectionHeight;
      this.scrollTotalDist = Math.max(1, this.scrollStartY - endY);
    }

    getTargetProgress(sectionTop) {
      return Math.max(0, Math.min(1, (this.scrollStartY - sectionTop) / this.scrollTotalDist));
    }

    startLoop() {
      if (this.loopActive) return;
      this.loopActive = true;
      this.renderRaf = requestAnimationFrame(this.boundTick);
    }

    stopLoop() {
      this.loopActive = false;
      if (this.renderRaf) {
        cancelAnimationFrame(this.renderRaf);
        this.renderRaf = null;
      }
    }

    tick() {
      if (!this.loopActive) return;

      const rect = this.section.getBoundingClientRect();
      const inView = rect.top <= this.viewportHeight && rect.bottom >= 0;

      if (!inView) {
        this.stopLoop();
        return;
      }

      this.targetProgress = this.getTargetProgress(rect.top);

      const diff = this.targetProgress - this.currentProgress;
      if (Math.abs(diff) < FloatingImages.PROGRESS_EPSILON) {
        this.currentProgress = this.targetProgress;
      } else {
        this.currentProgress += diff * FloatingImages.LERP_FACTOR;
      }

      this.applyTransforms(this.currentProgress);

      if (Math.abs(this.targetProgress - this.currentProgress) < FloatingImages.PROGRESS_EPSILON) {
        this.stopLoop();
        return;
      }

      this.renderRaf = requestAnimationFrame(this.boundTick);
    }

    applyTransforms(progress) {
      const remaining = 1 - progress;

      const buildTransform = (tx, ty, scaleEnd, rotation) => {
        const scale = 1 + (scaleEnd - 1) * remaining;
        return `translate3d(${tx * remaining}px, ${ty * remaining}px, 0) scale(${scale}) rotateZ(${rotation * remaining
          }deg)`;
      };

      const firstImage = this.images[0];
      if (firstImage) {
        firstImage.style.transform = buildTransform(
          -(this.sectionWidth * 0.02),
          -(this.sectionHeight * 0.4),
          0.5,
          -19.2102
        );
      }

      if (this.images.length > 2) {
        const lastImage = this.images[this.images.length - 1];
        lastImage.style.transform = buildTransform(this.sectionWidth * 0.025, -(this.sectionHeight * 0.35), 0.3, 23);
      }
    }
  }
  customElements.define('floating-images', FloatingImages, { extends: 'div' });
}